import type { ComponentType, PropsWithChildren } from 'react';
import type React from 'react';

import { useLocation } from 'react-router';

import { TrackingContext } from '@change-corgi/core-react/tracking';

import { useCurrentRouteConfig } from '#src/app/routes';
import { useQueryParamValue } from '#src/app/shared/hooks/location';
import { useCookiePref } from '#src/app/shared/hooks/session';
import type { ErrorStateDetails } from '#src/app/shared/utils/async';
import { isError, isLoaded } from '#src/app/shared/utils/async';

import type { PsfPromoteOrSharePageCombinedState, PsfPromoteOrSharePagePrefetchedContext } from './hook';
import { usePageContext } from './hook';
import { PsfPromoteOrSharePageDataContextProvider } from './pageData';
import { PsfPromoteOrSharePageStateContextProvider } from './pageState';

export type ErrorHandlerProps = { context: ErrorStateDetails<PsfPromoteOrSharePageCombinedState> };

type Props = {
	slug: string;
	prefetchedData: PsfPromoteOrSharePagePrefetchedContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export const PsfPromoteOrSharePageContextProvider: React.FC<PropsWithChildren<Props>> = ({
	children,
	slug,
	prefetchedData,
	errorHandler: ErrorHandler,
	loadingFallback,
}) => {
	const pageContextState = usePageContext(slug, prefetchedData);
	const sourceLocation = useQueryParamValue('source_location');
	const cookiePref = useCookiePref('preferences');
	const { pathname } = useLocation();
	const routeConfig = useCurrentRouteConfig();

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;
	const { pageData } = pageContextState;

	return (
		<PsfPromoteOrSharePageDataContextProvider pageData={pageData}>
			<PsfPromoteOrSharePageStateContextProvider>
				<TrackingContext
					properties={{
						petition_id: `petition_${pageData.data.petition.id}`,
						combo_bandit_variant: pageData.data.bandits.psfCombo.variant?.variantName,
						image: !!pageData.data.petition.photo,
						signature_count: pageData.data.petition.signatureState.signatureCount.displayed,
						source_location: sourceLocation,
						route_id: routeConfig?.config.id,
						pathname,
						context: cookiePref ? pageData.data.bandits.psfCombo.comboExpVariant : undefined, // Bandits R7 Experiment Context
						content:
							cookiePref && pageData.data.bandits.psfCombo.comboExpVariant
								? JSON.stringify(pageData.data.chipInAmount)
								: undefined, // Bandits R7 Experiment Content - Combo Amount
					}}
				>
					{children}
				</TrackingContext>
			</PsfPromoteOrSharePageStateContextProvider>
		</PsfPromoteOrSharePageDataContextProvider>
	);
};
